import { Badge } from '../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { FileSearch } from 'lucide-react';

/**
 * Soft "Verify N page(s)" review HINT for an Intake Hub bill row.
 * Driven by Parser V2.2 Pass N / Pass C (`ai_page_attestation.uncovered_pages`,
 * falling back to `parse_completeness.gaps`). It is deliberately NOT a failure
 * badge — a flagged page is often a remittance stub or duplicate summary with no
 * extractable rows, so this only nudges a reviewer to glance at the page(s).
 * Renders nothing when every page is accounted for.
 */
export default function PageGapChip({ attestation, completeness, docId }) {
  const pages = (attestation?.uncovered_pages && attestation.uncovered_pages.length
    ? attestation.uncovered_pages
    : null);
  const gapTabs = completeness?.gaps || [];
  if ((!pages || pages.length === 0) && gapTabs.length === 0) return null;

  const count = pages ? pages.length : gapTabs.length;
  const label = pages ? `Verify ${count} page${count > 1 ? 's' : ''}` : 'Verify pages';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge
          variant="outline"
          className="rounded-none text-[10px] px-1 py-0 bg-amber-50 text-amber-800 border-amber-200 shrink-0 flex items-center gap-0.5 cursor-pointer hover:bg-amber-100"
          title="Coverage hint — a page with billing signal wasn't fully captured. Often a stub/summary; quick check."
          onClick={(e) => { e.stopPropagation(); }}
          data-testid={`page-gap-chip-${docId}`}
        >
          <FileSearch className="h-2.5 w-2.5" /> {label}
        </Badge>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0 rounded-none" data-testid={`page-gap-popover-${docId}`}>
        <div className="px-3 py-2 border-b bg-amber-50 flex items-center gap-2">
          <FileSearch className="h-4 w-4 text-amber-700" />
          <span className="text-sm font-semibold text-amber-900">Page coverage hint</span>
        </div>
        <div className="p-3 space-y-2">
          <p className="text-xs text-muted-foreground">
            These page(s) carry meter/usage/charge signal in the OCR but weren't fully
            reflected in the parse. Many are remittance stubs or duplicate summaries —
            a quick look confirms nothing's missing.
          </p>
          {pages && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Pages to check</div>
              <div className="flex flex-wrap gap-1">
                {pages.map((p) => (
                  <span key={p} className="text-[11px] font-mono border border-amber-200 bg-amber-50 px-1.5 py-0.5" data-testid={`page-gap-pageno-${docId}-${p}`}>
                    p.{p}
                  </span>
                ))}
              </div>
            </div>
          )}
          {gapTabs.length > 0 && (
            <div className="text-[10px] text-muted-foreground">
              Signals: {gapTabs.join(', ')}
            </div>
          )}
          {attestation?.mode && (
            <div className="text-[10px] text-muted-foreground pt-1 border-t">
              Coverage pass: {attestation.mode.replace(/_/g, ' ')}
              {attestation.rounds ? ` · ${attestation.rounds} round(s)` : ''}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
